import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckSquare, Plus, Trash2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRIORITY_COLORS = { high: 'text-red-400', medium: 'text-yellow-400', low: 'text-blue-400' };

export default function TasksTab({ deal, dealId, onRefresh }) {
  const tasks = deal.tasks || [];
  const [showNew, setShowNew] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', due_date: '', priority: 'medium', description: '' });

  const handleAdd = async () => {
    if (!newTask.title.trim()) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API}/crm/tasks`, {
        deal_id: dealId, title: newTask.title, description: newTask.description || undefined,
        due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : undefined,
        priority: newTask.priority,
      });
      setNewTask({ title: '', due_date: '', priority: 'medium', description: '' });
      setShowNew(false);
      toast.success('Task created');
      onRefresh();
    } catch (e) { toast.error('Failed to create task'); }
  };

  const handleToggle = async (taskId, isCompleted) => {
    try {
      await axios.put(`${API}/crm/tasks/${taskId}?is_completed=${!isCompleted}`);
      onRefresh();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleDelete = async (taskId) => {
    try { await axios.delete(`${API}/crm/tasks/${taskId}`); onRefresh(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const now = new Date();
  const isOverdue = (task) => {
    if (task.is_completed || !task.due_date) return false;
    return new Date(task.due_date) < now;
  };

  const pending = tasks.filter(t => !t.is_completed).sort((a, b) => {
    const pa = { high: 0, medium: 1, low: 2 }; return (pa[a.priority] || 1) - (pa[b.priority] || 1);
  });
  const completed = tasks.filter(t => t.is_completed);

  return (
    <div className="max-w-3xl space-y-4" data-testid="tasks-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium flex items-center gap-2">
          <CheckSquare className="h-5 w-5 text-green-400" /> Tasks
          <span className="text-xs text-gray-500">{pending.length} pending</span>
        </h2>
        <Button onClick={() => setShowNew(!showNew)} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-task-btn">
          <Plus className="h-4 w-4 mr-1" />Add Task
        </Button>
      </div>

      {showNew && (
        <div className="bg-navy-800/50 rounded-xl p-4 border border-purple-500/20 space-y-3">
          <input value={newTask.title} onChange={e => setNewTask(p => ({...p, title: e.target.value}))} placeholder="Task title..."
            className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Due Date</label>
              <input type="date" value={newTask.due_date} onChange={e => setNewTask(p => ({...p, due_date: e.target.value}))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Priority</label>
              <select value={newTask.priority} onChange={e => setNewTask(p => ({...p, priority: e.target.value}))}
                className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none">
                <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button onClick={() => setShowNew(false)} variant="ghost" size="sm" className="text-gray-400">Cancel</Button>
            <Button onClick={handleAdd} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">Create</Button>
          </div>
        </div>
      )}

      {/* Pending Tasks */}
      <div className="space-y-1">
        {pending.map(task => (
          <div key={task.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${isOverdue(task) ? 'bg-red-500/5 border-red-500/20' : 'bg-navy-800/50 border-white/5'}`} data-testid={`task-row-${task.id}`}>
            <button onClick={() => handleToggle(task.id, task.is_completed)}
              className="w-5 h-5 rounded border border-white/20 flex items-center justify-center hover:border-green-400 hover:bg-green-500/10 transition flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{task.title}</div>
              {task.due_date && (
                <div className={`text-xs flex items-center gap-1 ${isOverdue(task) ? 'text-red-400' : 'text-gray-500'}`}>
                  {isOverdue(task) ? <AlertCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                  {new Date(task.due_date).toLocaleDateString()}
                </div>
              )}
            </div>
            <span className={`text-xs ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>{task.priority}</span>
            {task.created_by === 'workflow' && <span className="text-xs bg-purple-500/10 text-purple-400 px-1.5 py-0.5 rounded">auto</span>}
            <button onClick={() => handleDelete(task.id)} className="text-gray-500 hover:text-red-400 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {pending.length === 0 && <div className="text-center py-4 text-gray-500 text-sm">No pending tasks</div>}
      </div>

      {/* Completed */}
      {completed.length > 0 && (
        <div>
          <h3 className="text-xs text-gray-500 uppercase mb-2">Completed ({completed.length})</h3>
          <div className="space-y-1">
            {completed.slice(0, 10).map(task => (
              <div key={task.id} className="flex items-center gap-3 px-4 py-2 rounded-lg bg-navy-800/20 border border-white/5">
                <button onClick={() => handleToggle(task.id, task.is_completed)}
                  className="w-5 h-5 rounded bg-green-500/20 border border-green-500/30 flex items-center justify-center flex-shrink-0">
                  <CheckSquare className="h-3 w-3 text-green-400" />
                </button>
                <span className="text-sm text-gray-500 line-through truncate">{task.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
